import { Nullable, TestStatus } from "./api/types";

export enum USER_ROLES {
    LOCAL_ADMIN = "LOCAL_ADMIN",
    HCP = "HCP",
    PHYSIOLOGIST = "PHYSIOLOGIST",
    CLINICIAN = "CLINICIAN",
    SUPER_ADMIN = "SUPER_ADMIN",
}

export type UIElement =
    | "Interpretation confirmation"
    | "Interpretation recalculate"
    | "Interpretation edit"
    | "System edit"
    | "Severity edit"
    | "Anaerobic smoothing"
    | "Anaerobic VAT"
    | "Anaerobic RCP"
    | "Inbar12 Slope"
    | "Inbar12 Peak"
    | "Inbar12 Response"
    | "Inbar12 Smoothing"
    | "Wasserman9 Slope"
    | "Wasserman9 Peak"
    | "Wasserman9 Smoothing"
    | "Report edit"
    | "Report save"
    | "Report sign"
    | "Navigation bar";

export interface UserFromAPI {
    avatar: string;
    username: string;
    role: USER_ROLES;
    "blocked ui elements": null | UIElement[];
}
export interface User {
    avatar: string;
    userName: string;
    role: USER_ROLES;
    blockedUIElements: UIElement[];
}

export interface Test {
    id: number;
    navbarStatus: number;
}

export interface TestSearchResult {
    firstName: string;
    lastName: string;
    personId: string;
    status: TestStatus;
    testDate: Date;
    testId: number;
}

export interface TestSearchResultFromAPI {
    first_name: string;
    last_name: string;
    person_id: string;
    test_status: TestStatus;
    test_date: string;
    test_id: number;
}

export enum InputTypes {
    TEXT = "text",
    NUMBER = "number",
    DATE = "date",
    SELECT = "select",
    RADIO = "radio",
    FEET_INCHES = "feet_inches",
}

export type InputSingleValue = string | number;

export type SelectOption =
    | {
          id: string;
          value: InputSingleValue;
          label: string;
      }
    | string;

export type NumericValidation = {
    min?: number;
    max?: number;
    step?: number;
    isInteger?: boolean;
    decimalPlaces?: number;
};

export type DataInputFormField = {
    id: string;
    label?: string;
    required: boolean;
    error?: string;
    secondaryError?: string;
    disabled?: boolean;
    withSecondaryValue?: boolean;
    isSecondaryInputEditable?: boolean;
    valueToDisplay?: Nullable<InputSingleValue>;
    tooltipText?: string;
    scale?: number;
    isSmallInput?: boolean;
    inputClassName?: string;
} & (
    | {
          type: InputTypes.TEXT;
          value: Nullable<string>;
          originalValue?: Nullable<string>;
          secondaryValue?: Nullable<string>;
      }
    | {
          type: InputTypes.NUMBER;
          value: Nullable<number>;
          originalValue?: Nullable<number>;
          secondaryValue?: Nullable<number>;
          validation?: NumericValidation;
      }
    | {
          type: InputTypes.DATE;
          value: Nullable<string>;
          originalValue?: Nullable<string>;
          secondaryValue?: Nullable<string>;
      }
    | ({
          type: InputTypes.SELECT;
          options: Array<SelectOption>;
      } & (
          | {
                multiple: true;
                value: Array<InputSingleValue>;
                originalValue?: Array<InputSingleValue>;
                secondaryValue?: Array<InputSingleValue>;
            }
          | {
                multiple?: false;
                value: Nullable<InputSingleValue>;
                originalValue?: Nullable<InputSingleValue>;
                secondaryValue?: Nullable<InputSingleValue>;
            }
      ))
    | {
          type: InputTypes.RADIO;
          value: Nullable<InputSingleValue>;
          options: Array<SelectOption>;
          originalValue?: Nullable<InputSingleValue>;
          secondaryValue?: Nullable<InputSingleValue>;
      }
    | {
          type: InputTypes.FEET_INCHES;
          value: Nullable<number>;
          feetValue: Nullable<number>;
          inchesValue: Nullable<number>;
          originalValue?: Nullable<number>;
      }
);

export interface RPEFormField {
    id: string;
    scale: DataInputFormField;
    value: DataInputFormField;
}

export type FormField = DataInputFormField | RPEFormField;

export interface FormHandle<T> {
    getFormData: () => T;
    onSubmit: () => Promise<{ success: boolean; testId?: string }>;
    isDirty: boolean;
}

export enum TableConfiguration {
    DEFAULT = "DEFAULT",
    PEAK_VALUES = "PEAK_VALUES",
    SLOPES = "SLOPES",
    RESPONSES = "RESPONSES",
    POST_SPIROMETRY = "POST_SPIROMETRY",
    PRE_SPIROMETRY = "PRE_SPIROMETRY",
    DYNAMIC_LOOP = "DYNAMIC_LOOP",
    INTERPRETATION = "INTERPRETATION",
    LAB_RESULTS = "LAB_RESULTS",
    PERSON = "PERSON",
}

export type TableData = {
    parameterName: string;
    measured?: number | string;
    predicted?: string;
    precentOfPredicted?: number;
    prePostPrecent?: number;
    threshold?: number;
    status?: PeakStatus;
    statusColor?: string;
};

export enum PeakStatus {
    UNDETERMINED = "Undetermined",
    HIGH = "High",
    NORMAL = "Normal",
    LOW = "Low",
}

export enum GraphSystems {
    METABOLIC,
    CARDIAC,
    VENTILATORY,
    GAS_EXCHANGE,
    VENTILATORY_EQUIVALENTS,
}

type XY = {
    x: number;
    y: number;
};

type XYs = XY[];

type Ticks = {
    count: number;
    max: number;
    min: number;
    step: number;
    precision: number;
};

type LabelXCoordinates = {
    xxl: number;
    default: number;
};

export type StatusData = {
    status: PeakStatus;
    status_color: string;
    status_label?: string;
};

export type TestGraph<T extends GraphType = any> = (T extends "wasserman-9"
    ? {
          response1_y_label_units: string;
          response2_y_label_units: string;
      }
    : {}) & {
    graph_id: string;
    peak1_measured?: { x: number; y: number };
    peak1_predicted?: number;
    peak1_predicted_high?: number;
    peak1_predicted_low?: number;
    peak1_status?: PeakStatus;
    peak1_status_color?: string;
    peak1_status_title?: string;
    peak1_other_statuses?: StatusData[];
    peak1_predicted_label?: string;
    peak1_predicted_label_start_x_value?: LabelXCoordinates;
    peak1_predicted_label_end_x_value?: LabelXCoordinates;
    peak2_measured?: { x: number; y: number };
    peak2_predicted?: number;
    peak2_predicted_high?: number;
    peak2_predicted_low?: number;
    peak2_status?: PeakStatus;
    peak2_status_color?: string;
    peak2_other_statuses?: StatusData[];
    peak2_predicted_label?: string;
    peak2_predicted_label_start_x_value?: LabelXCoordinates;
    peak2_predicted_label_end_x_value?: LabelXCoordinates;
    peak3_measured?: { x: number; y: number };
    peak3_predicted?: number;
    peak3_predicted_high?: number;
    peak3_predicted_low?: number;
    peak3_status?: PeakStatus;
    peak3_status_color?: string;
    peak3_other_statuses?: StatusData[];
    peak3_predicted_label?: string;
    peak3_predicted_label_start_x_value?: LabelXCoordinates;
    peak3_predicted_label_end_x_value?: LabelXCoordinates;
    rcp_value?: number;
    response1_norm_high_xy?: XYs;
    response1_norm_low_xy?: XYs;
    response1_y_ticks?: Ticks;
    response1_status?: PeakStatus;
    response1_status_color?: string;
    response1_label_color?: string;
    response1_other_statuses?: StatusData[];
    slope1_status?: PeakStatus;
    slope1_status_color?: string;
    slope1_label_color?: string;
    slope1_other_statuses?: StatusData[];
    slope_status_title?: string;
    // response1_y_min_max: { min: number; max: number };
    response1_xy?: XYs;
    [key: `response1_xy_${number}`]: XYs | undefined;
    [key: `response1_xy_${number}_color`]: string | undefined;
    response1_y_label: string;
    response1_y_label_no_units: string;
    response1_y_label_pixel_length?: number;
    response1_y_label_dot_offset?: {
        sm: number;
        md: number;
        lg: number;
        xl: number;
        "compare-primary": number;
        "compare-secondary": number;
        xxl: number;
    };
    response2_norm_high_xy?: XYs;
    response2_norm_low_xy?: XYs;
    response2_status?: PeakStatus;
    response2_status_color?: string;
    response2_label_color?: string;
    response2_other_statuses?: StatusData[];
    slope2_status?: PeakStatus;
    slope2_status_color?: string;
    slope2_label_color?: string;
    slope2_other_statuses?: StatusData[];
    // response2_y_min_max?: { min: number; max: number };
    response2_xy?: XYs;
    response2_y_ticks: Ticks;
    [key: `response2_xy_${number}`]: number[];
    [key: `response2_xy_${number}_color`]: string | undefined;
    [key: `horizontal${number}`]:
        | { label: string; y_value: number }
        | undefined;
    [key: `vertical${number}`]: { label: string; x_value: number } | undefined;
    response2_y_label?: string;
    response2_y_label_no_units: string;
    response2_y_label_pixel_length?: number;
    response2_y_label_dot_offset?: {
        sm: number;
        md: number;
        lg: number;
        xl: number;
        "compare-primary": number;
        "compare-secondary": number;
        xxl: number;
    };
    response3_label_color?: string;
    response3_xy?: XYs;
    response3_y_label?: string;
    response3_y_ticks?: Ticks;
    response3_status_color?: string;

    slope3_status?: PeakStatus;
    slope3_status_color?: string;
    slope3_label_color?: string;
    slope3_other_statuses?: StatusData[];

    slope_calculation_begin_value?: number;
    slope_calculation_end_value?: number;
    slope_value?: number;
    display_slope_value_label?: boolean;
    slope_value_label_position?: XY;
    title: string;
    trend1_xy?: XYs;
    trend1b_xy?: XYs;
    trend2b_xy?: XYs;
    trend2_xy?: XYs;
    vat_value?: number;
    x?: number[];
    x_label: string;
    x_ticks?: Ticks;
};

export type GraphType = "inbar-12" | "anaerobic-threshold" | "wasserman-9";

export type UpdateGraphData = Partial<
    Pick<
        TestGraph,
        | "vat_value"
        | "rcp_value"
        | "slope_calculation_begin_value"
        | "slope_calculation_end_value"
        | "peak1_status"
        | "peak2_status"
        | "peak3_status"
        | "response1_status"
        | "response2_status"
        | "slope1_status"
        | "slope2_status"
        | "slope3_status"
    >
>;
